import { AsyncPipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { NgxPaginationModule } from 'ngx-pagination';
import { OnlysuperuserDirective } from '../../../../shared/directives/onlysuperuser.directive';
import { PermisionDirective } from '../../../../shared/directives/permision.directive';
import { Search } from '../../../../shared/components/search/search';
import { catchError, map, Observable, switchMap, tap } from 'rxjs';
import { AssistanceService } from '../../../../core/models/assistanceservice.interface';
import { LayoutService } from '../../../../core/services/layout/layout';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { LoadingService } from '../../../../core/services/loading/loading';
import { Title } from '@angular/platform-browser';
import { AssistanceserviceService } from '../../../../core/services/assistanceservice/assistanceservice-service';
import { PAGINATION } from '../../../../core/constants/constants';
import { Filter, QueryGet } from '../../../../core/types/query-types';
import { AssistanceserviceForm } from '../assistanceservice-form/assistanceservice-form';
import { ServiceactivityService } from '../../../../core/services/serviceactivity/serviceactivity-service';
import { AssistanceserviceServiceactivityList } from '../../serviceactivity/assistanceservice-serviceactivity-list/assistanceservice-serviceactivity-list';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { ServiceactivityForm } from '../../serviceactivity/serviceactivity-form/serviceactivity-form';
import { TermPipe } from '../../../../shared/pipes/term.pipe';
import { TermKey } from '../../../../core/models/terminology.interface';

@Component({
  selector: 'app-assistanceservice-list',
  imports: [
    MatIcon,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgxPaginationModule,
    AsyncPipe,
    OnlysuperuserDirective,
    PermisionDirective,
    Search,
    AssistanceserviceServiceactivityList,
    MatMenuModule,
    MatDividerModule,
    TermPipe,
  ],
  templateUrl: './assistanceservice-list.html',
  styleUrl: './assistanceservice-list.scss',
})
export class AssistanceserviceList {
  public page = 1;
  total: number = 0;
  limit: number = 10;
  private sectorName: string | null = null;
  termKey = TermKey;
  assistanceservice: AssistanceService[] = [];
  asyncassistanceservice!: Observable<AssistanceService[]>;
  buscarassistanceservice: string = '';
  @Input() filtro: string = '';
  @Input() orden: string = '';
  showColumns: string[] = [
    'acciones',
    'id',
    'name',
    'components',
    'humanResources',
    'infraestructure',
    'fixedAsset',
    'locations',
    'lifePlan',
    'territory',
    'total',
  ];

  expandedRows = new Set<number>();

  constructor(
    private layoutService: LayoutService,
    private activatedRouter: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private assistanceserviceService: AssistanceserviceService,
    private serviceactivityService: ServiceactivityService,
    private loadingService: LoadingService,
    private title: Title,
  ) {
    this.title.setTitle('Servicios de asistencia');
  }

  ngOnInit(): void {
    this.layoutService.paginationLimit$
      .pipe(
        switchMap((limit) => {
          this.limit = limit;
          return this.activatedRouter.queryParamMap;
        }),
        switchMap((params) => {
          const pageNum = params.get('page') || '1';
          const search = params.get('search') || '';
          this.page = parseInt(pageNum);
          this.fetchAssistanceServices(PAGINATION.first_page, null, search);
          return this.layoutService.paginationLimit$;
        }),
      )
      .subscribe();

    this.layoutService.sectorSelected$.subscribe((sector) => {
      this.sectorName = sector?.name ?? null;
      this.fetchAssistanceServices(1);
    });
  }

  fetchAssistanceServices(page: number, filter: Filter | null = null, search = '') {
    const offset = page > 1 ? page * this.limit - this.limit : 0;
    const params: QueryGet = {
      order: '-id',
      limit: this.limit,
      offset,
      search,
      filters: [],
    };
    if (filter) {
      params.filters.push(filter);
    }
    const sectorName = this.layoutService.getSectorSelected()?.name;
    if (sectorName) {
      params.filters.push({ name: 'sector', value: sectorName } as Filter);
    }
    this.page = page;
    this.loadingService.show(true);
    this.asyncassistanceservice = this.assistanceserviceService.getAssistanceService(params).pipe(
      tap((data) => {
        this.assistanceservice = data.results;
        this.assistanceserviceService.setAssistanceService(data.results[0]);
        this.assistanceserviceService.setAssistanceServices(data.results);
        this.total = data.count;
      }),
      map((res) => {
        this.loadingService.show(false);
        return res.results;
      }),
      catchError((error) => {
        this.loadingService.show(false);
        const errorMsg = `¡Se ha producido un error, inténtelo nuevamente!`;
        this.toastr.error(errorMsg, 'Error!', {
          progressBar: true,
          timeOut: 5000,
        });
        this.router.navigate(['home']);
        return [];
      }),
    );
  }

  createAssistanceService() {
    this.assistanceserviceService.removeAssistanceServiceSelected();
    const dialogRef = this.dialog.open(AssistanceserviceForm, {
      width: '600px',
      height: 'auto',
      maxHeight: '80vh',
      disableClose: true,
      data: { fromList: true },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.fetchAssistanceServices(this.page);
    });
  }

  toggleActivities(assistanceService: AssistanceService): void {
    if (this.expandedRows.has(assistanceService.id)) {
      this.expandedRows.delete(assistanceService.id);
      return;
    }
    this.expandedRows.add(assistanceService.id);
  }

  isExpanded(id: number): boolean {
    return this.expandedRows.has(id);
  }

  updateAssistanceService(assistanceService: AssistanceService) {
    this.assistanceserviceService.setAssistanceService(assistanceService);
    const dialogRef = this.dialog.open(AssistanceserviceForm, {
      width: '600px',
      height: 'auto',
      maxHeight: '80vh',
      disableClose: true,
      data: { fromList: true },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.fetchAssistanceServices(this.page);
    });
  }

  addActivity(assistanceService: AssistanceService): void {
    this.assistanceserviceService.setAssistanceService(assistanceService);
    this.expandedRows.add(assistanceService.id);
    this.serviceactivityService.removeServiceActivitySelected();
    const dialogRef = this.dialog.open(ServiceactivityForm, {
      width: '600px',
      height: 'auto',
      maxHeight: '80vh',
      disableClose: true,
      panelClass: 'custom-dialog-container',
      data: {
        isCreate: true,
        assistanceId: assistanceService.id,
      },
    });
    dialogRef.afterClosed().subscribe(() => this.fetchAssistanceServices(this.page));
  }

  eliminate(assistanceService: AssistanceService): void {
    const confirmed = window.confirm(
      `¿Estás seguro de eliminar el registro: ${assistanceService.name}?`,
    );
    if (!confirmed) return;
    this.assistanceserviceService.delete(assistanceService.id).subscribe({
      next: () => {
        const successMsg = `Eliminado CORRECTAMENTE!`;
        this.toastr.success(successMsg, 'Exitoso!', {
          progressBar: true,
          timeOut: 5000,
        });
        this.fetchAssistanceServices(this.page);
      },
      error: (err) => {
        this.toastr.error('No se pudo eliminar', 'Error');
      },
    });
  }
}
